import React, { useEffect, useState, useRef } from "react";
import styles from "./UploadFirmware.module.css";
import useAuthToken from "../../../hooks/useAuthToken";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import config from "../../../settings/config";
import { fetchGetFirmwareFiles, deleteFile, downloadFile } from "./fetchUploadFirmware";


const UploadFirmware = () => {
    const navigate = useNavigate();
    const token = useAuthToken();
    const [dataFiles, setDataFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const { setToast } = useToast();

    const showToast = (message, type, time) => {
        setToast({ message, type });
        setTimeout(() => setToast(null), time);
    };

    const loadFiles = async () => {
        try {
            const files = await fetchGetFirmwareFiles(token);
            if (files !== null) {
                console.log("Полученные файлы:", files);
                setDataFiles(files);
            }
        } catch (error) {
            console.error("Ошибка при загрузке файлов:", error);
            showToast("Ошибка при загрузке списка файлов", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Проверка типа файла (опционально)
        const allowedTypes = ['application/octet-stream', 'application/x-binary', 'binary/octet-stream'];
        if (!allowedTypes.includes(file.type) && !file.name.match(/\.(bin|hex|fw|img)$/i)) {
            showToast("Выберите файл прошивки (bin, hex, fw, img)", "warning");
            return;
        }

        // Проверка размера файла (например, максимум 100MB)
        if (file.size > 100 * 1024 * 1024) {
            showToast("Файл слишком большой. Максимальный размер: 100MB", "warning");
            return;
        }

        await uploadFirmware(file);
        event.target.value = ''; // Сброс input
    };

    const uploadFirmware = async (file) => {
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${config.app.host}/api/v1/admin/uploadFirmware`, {
                method: "POST",
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Ошибка ${response.status}: ${errorText}`);
            }

            const result = await response.json();
            showToast("Файл успешно загружен", "success");

            // Обновляем список файлов после успешной загрузки
            await loadFiles();

        } catch (error) {
            console.error("Ошибка при загрузке файла:", error);
            showToast(`Ошибка загрузки: ${error.message}`, "error");
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = (fileName) => {
        // Реализация скачивания
        console.log("Скачать файл:", fileName);
        downloadFile(fileName, token);
    };

    const handleMainMenu = (device) => {
        navigate('/devices');
    };

    const handleDelete = async (fileName) => {
        if (window.confirm(`Удалить файл ${fileName}?`)) {
            console.log("Удалить файл:", fileName);

            try {
                const result = await deleteFile(fileName, token);

                if (result) {
                    showToast(`Файл ${fileName} успешно удален`, "success");
                    // Обновляем список файлов после удаления
                    await loadFiles();
                }

            } catch (error) {
                console.error("Ошибка при удалении файла:", error);
                showToast(`Ошибка при удалении файла: ${error.message}`, "error");
            }
        }
    };

    useEffect(() => {
        console.log("UPDATE");
        loadFiles();

        const interval = setInterval(() => {
            console.log("Обновление данных...");
            loadFiles();
        }, 15000);

        return () => clearInterval(interval);
    }, [token]);

    console.log("Data files:", dataFiles);

    if (loading) {
        return <div className={styles.container}>Загрузка...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3>Ваши прошивки</h3>
                <dev className={styles.actions}>
                    <button
                        className={styles.menuButton}
                        onClick={handleMainMenu}
                    >
                        Главное меню
                    </button>
                    <button
                        className={styles.uploadButton}
                        onClick={handleUploadClick}
                        disabled={uploading}
                    >
                        {uploading ? "Загрузка..." : "Загрузить прошивку"}
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        accept=".bin,.hex,.fw,.img,application/octet-stream"
                    />
                </dev>

            </div>

            {!dataFiles || dataFiles.length === 0 ? (
                <p className={styles.noDevices}>Нет прошивок</p>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Имя файла</th>
                                <th>Размер</th>
                                <th>Дата изменения</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dataFiles.map((dataFile) => (
                                <tr key={`${dataFile.fileName}-${dataFile.lastModified}`}>
                                    <td>{dataFile.fileName}</td>
                                    <td>{formatFileSize(dataFile.size)}</td>
                                    <td>{formatLocalDateTime(dataFile.lastModified)}</td>
                                    <td>
                                        <button
                                            className={styles.actionButton}
                                            onClick={() => handleDownload(dataFile.fileName)}
                                        >
                                            Скачать
                                        </button>
                                        <button
                                            className={styles.deleteButton}
                                            onClick={() => handleDelete(dataFile.fileName)}
                                        >
                                            Удалить
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};


// Универсальная функция для форматирования LocalDateTime
const formatLocalDateTime = (dateTime) => {
    if (!dateTime) return '-';

    try {
        let date;

        if (Array.isArray(dateTime)) {
            const [year, month, day, hour, minute, second] = dateTime;
            date = new Date(year, month - 1, day, hour, minute, second);
        }
        else if (typeof dateTime === 'object' && dateTime !== null) {
            const { year, month, day, hour, minute, second, nano } = dateTime;
            date = new Date(year, month - 1, day, hour, minute, second);
        }
        else if (typeof dateTime === 'string') {
            date = new Date(dateTime);
        }
        else if (typeof dateTime === 'number') {
            date = new Date(dateTime);
        }
        else {
            console.warn("Неизвестный формат даты:", dateTime);
            return String(dateTime);
        }

        if (isNaN(date.getTime())) {
            console.warn("Некорректная дата:", dateTime);
            return String(dateTime);
        }

        return date.toLocaleString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });

    } catch (error) {
        console.error("Ошибка форматирования даты:", error, dateTime);
        return String(dateTime);
    }
};

const formatFileSize = (bytes) => {
    if (bytes === 0 || bytes === undefined || bytes === null) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default UploadFirmware;